import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { Modal } from "react-bootstrap";
import { toast } from "sonner";
import { SubmitHandler, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useAcademicYear } from "../../../core/common/context/AcademicYearContext";
import withPermission from "../../../core/common/autorisations/withPermission";
import {
  Action,
  buildPermission,
  Resource,
} from "../../../core/common/autorisations/permissionsMap";
import { createPaymentPlan } from "../../../api/paiementPlans";

type Props = {
  show: boolean;
  onClose: () => void;
  onSaved?: () => void;
};

const CreatePaymentPlanModal = ({ show, onClose, onSaved }: Props) => {
  //Initialisation de l'année academmique actuelle
  const { selectedYear } = useAcademicYear();

  const createSchema = z.object({
    label: z.string().nonempty("Le libellé du niveau d'étude est requis"),
    echeance: z.string().nonempty("Veuillez sélectionner la date d'échéance"),
    amount: z.string().nonempty("Le montant est requis"),
    to_ignore: z.boolean().optional(), // si on peut ignorer ce plan de paiement, par défaut false
  });

  type FormFields = z.infer<typeof createSchema>;

  const {
    register,
    handleSubmit,
    clearErrors,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormFields>({
    resolver: zodResolver(createSchema),
  });

  //Traitement après validation du formulaire de création d'un niveau
  const onSubmit: SubmitHandler<FormFields> = async (data) => {
    try {
      const finalData = {
        ...data,
        due_date: data.echeance,
        to_ignore: data.to_ignore ?? false,
        id_academicYear: selectedYear?.id ?? "", // 👈 Ajout manuel ici
      };
      //Envoi des datas pour création vers le backend
      await createPaymentPlan(finalData);

      reset(); //On réinitialise les champs du formulaire
      if (onSaved) onSaved(); // On fait un reload de la liste

      toast.success("Niveau d'étude ajouté !"); //Toast de succès

      onClose(); // On ferme le modal
    } catch (error: any) {
      // Si erreur (Toast Error)
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          "Une erreur s'est produit, Réessayer plus tard",
      );
    }
  };

  return (
    <>
      <Modal show={show} onHide={() => onClose()}>
        <Modal.Header closeButton className="border-bottom">
          <Modal.Title>Nouveau Plan de Paiement</Modal.Title>
        </Modal.Header>
        <Modal.Body className="bg-white">
          {/* Message d'erreur serveur */}
          {errors.root && (
            <div className="alert alert-danger rounded-pill alert-dismissible fade show">
              {errors.root.message}
              <button
                type="button"
                className="btn btn-danger btn-sm"
                aria-label="Close"
                onClick={() => clearErrors("root")}
              >
                <i className="fas fa-xmark" />
              </button>
            </div>
          )}
          <form
            id="level-form"
            className="needs-validation"
            onSubmit={handleSubmit(onSubmit)}
          >
            <div className="row row-cols-xxl-5 row-cols-md-6">
              <div className="col-xxl-12 col-xl-12 col-md-12">
                <div className="mb-3">
                  <label className="form-label" htmlFor="validationServer01">
                    Libellé
                  </label>
                  <input
                    {...register("label")}
                    type="text"
                    className={
                      errors.label ? "form-control is-invalid" : "form-control"
                    }
                    placeholder="Ex: 1ère tranche ou Inscription"
                  />
                  {errors.label && (
                    <div className="invalid-feedback">
                      {errors.label.message}
                    </div>
                  )}
                </div>
              </div>

              <div className="col-xxl-6 col-xl-6 col-md-6">
                <div className="mb-3">
                  <label className="form-label" htmlFor="validationServer01">
                    Montant (F CFA)
                  </label>
                  <input
                    {...register("amount")}
                    type="text"
                    className={
                      errors.amount ? "form-control is-invalid" : "form-control"
                    }
                    placeholder="Ex: 150 000 FCFA"
                  />
                  {errors.amount && (
                    <div className="invalid-feedback">
                      {errors.amount.message}
                    </div>
                  )}
                </div>
              </div>

              <div className="col-xxl-6 col-xl-6 col-md-6">
                <div className="mb-3">
                  <label className="form-label" htmlFor="validationServer01">
                    Échéance
                  </label>
                  <input
                    {...register("echeance")}
                    type="date"
                    className={
                      errors.echeance
                        ? "form-control is-invalid"
                        : "form-control"
                    }
                    placeholder="Ex: 150 000 FCFA"
                  />
                  {errors.echeance && (
                    <div className="invalid-feedback">
                      {errors.echeance.message}
                    </div>
                  )}
                </div>
              </div>

              <div className="col-xxl-12 col-xl-12 col-md-12">
                <div className="form-check mb-3">
                  <input
                    {...register("to_ignore")}
                    className="form-check-input"
                    type="checkbox"
                    id="toIgnoreCheckbox"
                  />
                  <label className="form-check-label" htmlFor="toIgnoreCheckbox">
                   est non obligatoire ?
                 </label>
                </div>
              </div>
            </div>
          </form>
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="btn btn-light me-3"
            onClick={() => {
              reset();
              onClose();
            }}
          >
            Annuler
          </button>

          {isSubmitting ? (
            <button
              className="btn btn-primary-light"
              type="button"
              disabled={false}
            >
              <span
                className="spinner-border spinner-border-sm align-middle me-1"
                role="status"
                aria-hidden="true"
              />
              Chargement...
            </button>
          ) : (
            <button
              type="submit"
              form="level-form" // liaison au formulaire
              className="btn btn-primary"
              disabled={isSubmitting}
            >
              Enregistrer
            </button>
          )}
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default withPermission(
  CreatePaymentPlanModal,
  [buildPermission(Resource.PAYMENT_PLAN, Action.CREATE)],
  "masquer",
);
